/**
 * Permission Utilities
 * 권한 관련 유틸리티 함수
 */

import type { Permissions, MenuPermission, ButtonPermission } from '@/shared/stores/sessionStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { api } from '@/shared/api/client';

type TranslateFn = (key: string) => string | undefined | null;

/** My Lettaga 메뉴 키(/mylettaga, /mylettaga/members 등) */
export function isMyLettagaMenuKey(menuKey: string | null | undefined): boolean {
  const k = String(menuKey || '');
  return k === '/mylettaga' || k.startsWith('/mylettaga/');
}

/**
 * 부서에 My Lettaga(PRV025~028)가 저장돼 있는지.
 * permissions.mylSaved === true 일 때만 권한 적용.
 * false / undefined(구세션) → 미저장으로 보고 메뉴·버튼 통과.
 */
export function hasAnyMyLettagaMenuConfigured(permissions: Permissions | null | undefined): boolean {
  return permissions?.mylSaved === true;
}

/**
 * 현재 경로에서 메뉴 키 추출
 * 예: /entity/profile/123 → /entity
 */
export function getMenuKeyFromRoute(routePath: string): string | null {
  if (!routePath) return null;

  // Unified Settings (/settings/*) — reuse the ORIGINAL permission keys so the existing
  // menu-permission system (sidebar visibility, guards, button gates) keeps applying.
  // Bare '/settings' and personal sections resolve to null → always viewable (everyone has
  // a personal account); admin sections resolve to their original menuKey.
  if (routePath === '/settings' || routePath.startsWith('/settings/')) {
    const section = routePath.split('/').filter(Boolean)[1] || '';
    if (section === 'company' || section === 'billing' || section === 'entities') return '/pages/company-settings';
    if (section === 'users') return '/users';
    if (section === 'authorisation') return '/pages/authorization-settings';
    if (section === 'my-lettaga') return '/mylettaga/settings';
    return null; // account / security / notifications / container
  }

  // /entity 또는 /entity_my로 시작하는 경우
  if (routePath.startsWith('/entity_my')) {
    return '/entity_my';
  }
  if (routePath.startsWith('/entity')) {
    // canonical permission key
    return '/entity_my';
  }

  // Reminder는 하위 메뉴(2-depth) 단위로 권한키를 사용한다.
  if (routePath.startsWith('/reminder/')) {
    const segments = routePath.split('/').filter(Boolean);
    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }
    return '/reminder';
  }

  // pages 하위 메뉴도 2-depth 단위 권한키를 사용한다.
  if (routePath.startsWith('/pages/')) {
    const segments = routePath.split('/').filter(Boolean);
    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }
    return '/pages';
  }
  
  // Start Task(Task Manager)도 하위 메뉴(2-depth) 단위로 권한키를 사용할 수 있다.
  // 예: '/tasks/new' → '/tasks/new'
  if (routePath.startsWith('/tasks/')) {
    // T-Sign History / T-Filing History는 route path를 그대로 권한키로 사용
    if (routePath.startsWith('/tasks/tsign-history')) {
      return '/tasks/tsign-history';
    }
    if (routePath.startsWith('/tasks/tfiling-history')) {
      return '/tasks/tfiling-history';
    }
    const segments = routePath.split('/').filter(Boolean);
    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }
    return '/tasks';
  }

  // Marketplace: 하위 화면(subscribe, my-subscriptions, 상세 등)은 경로별로 구분
  // (기존에는 모두 /marketplace 로만 매핑되어 DB 부모 메뉴명이 두 하위 항목에 동일 적용되는 문제가 있었음)
  if (routePath.startsWith('/marketplace/')) {
    const segments = routePath.split('/').filter(Boolean);
    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }
    return '/marketplace';
  }

  // My Lettaga: Members / Chat / Settings 는 2-depth 권한키 (PRV026~028)
  if (routePath.startsWith('/mylettaga/')) {
    const segments = routePath.split('/').filter(Boolean);
    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }
    return '/mylettaga';
  }

  // Company / Billing Settings: /pages/company-settings
  if (routePath.startsWith('/pages/')) {
    const segments = routePath.split('/').filter(Boolean);
    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }
    return '/pages';
  }

  // 첫 번째 경로 세그먼트 추출
  const segments = routePath.split('/').filter(Boolean);
  if (segments.length === 0) return null;
  
  return `/${segments[0]}`;
}

/**
 * 경로 기준 DB 메뉴명(L_MENU.MENU_NAME) 조회
 * - 없거나 공백이면 null
 */
export function getDbMenuNameByRoute(
  permissions: Permissions | null,
  routePath: string
): string | null {
  if (!permissions?.menus) return null;
  const menuKey = getMenuKeyFromRoute(routePath);
  if (!menuKey) return null;
  const raw = (permissions.menus[menuKey] as any)?.menuName;
  const normalized = raw ? String(raw).trim() : '';
  return normalized || null;
}

/**
 * 번역 시도 — 키가 그대로 돌아오면(번역 없음) null.
 * createSyncT 는 번역이 없을 때 키 문자열을 그대로 반환한다.
 */
function tryTranslate(t: TranslateFn | undefined, key: string): string | null {
  if (!t || !key) return null;
  const raw = t(key);
  const normalized = raw ? String(raw).trim() : '';
  if (!normalized) return null;
  // 번역을 못 찾은 경우: 키 자체 또는 'menu.' 접두사가 남은 문자열이 돌아온다
  if (normalized === key || normalized.startsWith('menu.')) return null;
  return normalized;
}

/**
 * 메뉴 표시명 해석
 * 우선순위: DB명의 번역(menu.<DB명>) > DB명 > i18n(titleKey) > fallback
 *
 * DB(L_MENU.MENU_NAME)는 CLIENT_CD 가 없는 전역 값이라 언어별 값을 가질 수 없다.
 * 그래서 DB명을 그대로 쓰지 않고 먼저 번역 키(menu.<DB명>)로 한 번 찾아본다.
 *  - 번역이 있으면  → 화면 언어로 표시 (예: 'Billing Settings' → '청구 설정')
 *  - 번역이 없으면  → DB명을 그대로 표시 (어드민에서 임의로 바꾼 이름도 그대로 노출)
 * 이렇게 하면 다국어와 어드민 메뉴명 변경 기능이 함께 살아 있다.
 */
export function resolveMenuDisplayName(options: {
  permissions: Permissions | null;
  routePath: string;
  t?: TranslateFn;
  titleKey?: string | null;
  fallback?: string;
}): string {
  const { permissions, routePath, t, titleKey, fallback = '' } = options;

  const dbName = getDbMenuNameByRoute(permissions, routePath);
  if (dbName) {
    return tryTranslate(t, `menu.${dbName}`) || dbName;
  }

  if (titleKey && t) {
    const translated = t(titleKey);
    const normalized = translated ? String(translated).trim() : '';
    if (normalized) return normalized;
  }
  return fallback;
}

/**
 * 특정 메뉴의 버튼 권한을 객체로 변환
 * 
 * @param permissions - 전체 권한 객체
 * @param routePath - 현재 경로 (예: /entity, /entity_my)
 * @returns 버튼 이름을 키로 하는 권한 객체
 */
export function buildButtonPermissions(
  permissions: Permissions | null,
  routePath: string
): Record<string, ButtonPermission> {
  const result: Record<string, ButtonPermission> = {};
  
  if (!permissions || !permissions.isAuthEnabled) {
    return result;
  }
  
  const menuKey = getMenuKeyFromRoute(routePath);
  if (!menuKey) {
    return result;
  }
  
  const menuPermission = permissions.menus[menuKey];
  if (!menuPermission) {
    return result;
  }
  
  // menuPermission 객체에서 버튼 권한 추출
  // view는 제외하고, ButtonPermission 타입인 것만 추출
  Object.keys(menuPermission).forEach((key) => {
    if (key === 'view') return; // view는 제외
    
    const value = menuPermission[key];
    
    // ButtonPermission 타입인지 확인
    if (value && typeof value === 'object' && 'buttonCd' in value && 'privCd' in value && 'allowed' in value) {
      result[key] = value as ButtonPermission;
    }
  });
  
  return result;
}

/**
 * 특정 버튼의 권한 확인
 * 
 * @param permissions - 전체 권한 객체
 * @param routePath - 현재 경로
 * @param buttonName - 버튼 이름 (예: 'Add Entity', 'Modify', 'Delete')
 * @returns 권한이 있으면 true, 없으면 false
 */
export function checkButtonPermission(
  permissions: Permissions | null,
  routePath: string,
  buttonName: string
): boolean {
  // 1) 권한 객체가 아예 없으면 (로그인 전 등) 일단 허용
  if (!permissions) {
    return true;
  }

  // 2) 클라이언트 설정 상 Authorization 기능이 꺼져 있으면 프론트도 모두 허용
  if (!permissions.isAuthEnabled) {
    return true;
  }

  // 2.5) My Lettaga: 부서에 ML 메뉴 권한 미설정이면 Invite/New Chat 등 통과
  const menuKey = getMenuKeyFromRoute(routePath);
  if (isMyLettagaMenuKey(menuKey) && !hasAnyMyLettagaMenuConfigured(permissions)) {
    return true;
  }
  
  // 3) 해당 메뉴의 버튼 권한 조회
  const buttonPermissions = buildButtonPermissions(permissions, routePath);
  const buttonPermission = buttonPermissions[buttonName];
  
  // 4) 버튼 권한이 정의되지 않은 경우 기본값: 거부
  if (!buttonPermission) {
    return false;
  }
  
  // 5) 명시적으로 allowed=true 인 버튼만 허용
  return buttonPermission.allowed === true;
}

/**
 * 서버 세션 기준 버튼 권한 확인.
 *
 * 클라이언트 sessionStore(=/auth/me 응답 복사본)는 변조될 수 있으므로,
 * Confirm/실행 직전에 서버에 다시 물어 서버 세션의 권한으로 판단한다.
 * - 서버는 req.session.user.permissions 로만 판단 → 변조된 값 무시
 * - 정상 사용자는 세션 값이 동일하므로 기존과 같은 결과를 받는다.
 *
 * @returns 허용이면 true, 거부면 false
 * @throws API 호출 실패 시 (호출부에서 로컬 폴백 처리)
 */
export async function checkButtonPermissionOnServer(
  menuKey: string,
  buttonName: string
): Promise<boolean> {
  const res = await api.post<{ allowed?: boolean }>('/auth/check-button-permission', {
    menuKey,
    buttonName,
  });
  const allowed = (res as any)?.allowed ?? (res as any)?.data?.allowed;
  return allowed === true;
}

/**
 * 세션에 permissions 가 없을 때만 백엔드에서 재로드하여 보정하는 헬퍼
 * - 로그인 직후 또는 세션 초기화 이후 첫 버튼 클릭 시만 호출됨
 */
export async function ensurePermissionsLoaded(): Promise<Permissions | null> {
  const sessionStore = useSessionStore();
  const perms = sessionStore.getPermissions as Permissions | null;

  // 이미 로드되어 있고 isAuthEnabled 필드도 있으면 그대로 사용
  if (perms && typeof perms.isAuthEnabled === 'boolean' && perms.menus) {
    // ✅ 현재 세션의 엔티티 메뉴 권한 스냅샷 로그
    try {
      const entityMenusForLog = ['/entity', '/entity_my'];
      const snapshot: any = {
        isAuthEnabled: perms.isAuthEnabled,
        menuKeys: Object.keys(perms.menus),
        menus: {}
      };

      entityMenusForLog.forEach((key) => {
        const menu = (perms.menus as any)[key];
        if (!menu) return;

        snapshot.menus[key] = {
          view: menu.view,
          buttons: Object.keys(menu)
            .filter((btnKey) => btnKey !== 'view')
            .reduce((acc: any, btnKey) => {
              const bp = menu[btnKey];
              acc[btnKey] = {
                allowed: bp?.allowed,
                privCd: bp?.privCd,
              };
              return acc;
            }, {}),
        };
      });

      // eslint-disable-next-line no-console
      console.log('[Permission] current permissions snapshot', snapshot);
    } catch (e) {
      // 스냅샷 로깅 실패는 무시
    }

    return perms;
  }

  try {
    // /auth/me 호출을 통해 최신 사용자/권한 정보 재조회
    const res = await api.get<any>('/auth/me');
    const user = (res as any)?.user || (res as any)?.data?.user;

    if (user?.permissions) {
      sessionStore.setUser(user);
      sessionStore.setPermissions(user.permissions);
      return user.permissions as Permissions;
    }
  } catch (error) {
    console.error('[Permission] Failed to reload permissions from /auth/me:', error);
  }

  // 퍼미션이 여전히 없으면 isAuthEnabled=false 로 간주 (권한 기능 OFF → 모두 허용)
  return {
    isAuthEnabled: false,
    menus: {},
    SpecialFeature: {},
  } as Permissions;
}

/**
 * Special Feature 권한 확인
 * 
 * @param permissions - 전체 권한 객체
 * @param featureName - Special Feature 이름 (예: 'Synchronise', 'Start Task', 'Marketplace Subscribe')
 * @returns 권한이 있으면 true, 없으면 false
 */
export function checkSpecialFeature(
  permissions: Permissions | null,
  featureName: string
): boolean {
  if (!permissions || !permissions.isAuthEnabled) {
    // 권한 시스템이 비활성화된 경우 기본적으로 허용
    return true;
  }
  
  const feature = permissions.SpecialFeature[featureName];
  if (!feature) {
    // Special Feature가 정의되지 않은 경우 기본적으로 허용
    return true;
  }
  
  return feature.use === true;
}

/**
 * 메뉴 view 권한 확인
 * 
 * @param permissions - 전체 권한 객체
 * @param routePath - 현재 경로
 * @returns 메뉴 접근 권한이 있으면 true, 없으면 false
 */
export function checkMenuViewPermission(
  permissions: Permissions | null,
  routePath: string
): boolean {
  if (!permissions || !permissions.isAuthEnabled) {
    // 권한 시스템이 비활성화된 경우 기본적으로 허용
    return true;
  }
  
  const menuKey = getMenuKeyFromRoute(routePath);
  if (!menuKey) {
    return true;
  }

  // Dashboard: L_MENU 미등록 클라이언트도 기본 허용 (집계 홈)
  if (menuKey === '/dashboard') {
    const menuPermission = permissions.menus[menuKey];
    if (!menuPermission) {
      return true;
    }
    return menuPermission.view === true;
  }

  // My Lettaga: 부서에 ML 메뉴 권한 미설정이면 view 통과
  if (isMyLettagaMenuKey(menuKey) && !hasAnyMyLettagaMenuConfigured(permissions)) {
    return true;
  }

  let menuPermission = permissions.menus[menuKey];
  // 백엔드에 /marketplace/subscribe 등 개별 키가 없으면 부모 /marketplace 권한으로 평가
  if (!menuPermission && menuKey.startsWith('/marketplace/')) {
    menuPermission = permissions.menus['/marketplace'];
  }
  if (!menuPermission) {
    return false;
  }

  return menuPermission.view === true;
}

export const DEFAULT_NO_ENTITY_PERMISSION_PATH = '/entity-no-permission';

/** jurisdiction 기준 Entity List 경로 */
export function resolveEntityListPath(jurisdiction?: string | null | undefined): string {
  const j = String(jurisdiction ?? '').trim().toUpperCase();
  return j === 'MY' || j === 'MALAYSIA' ? '/entity_my' : '/entity';
}

/**
 * 로그인·루트·메뉴 view 권한 기준 기본 랜딩 경로 (local/dev/production 공통).
 * - isAuthEnabled=false → /dashboard
 * - isAuthEnabled=true  → Dashboard view → Entity view → /entity-no-permission
 */
export function resolveDefaultLandingPath(
  permissions: Permissions | null | undefined,
  jurisdiction?: string | null | undefined,
): string {
  const entityPath = resolveEntityListPath(jurisdiction);
  if (!permissions?.isAuthEnabled) {
    return '/dashboard';
  }
  if (checkMenuViewPermission(permissions, '/dashboard')) {
    return '/dashboard';
  }
  if (checkMenuViewPermission(permissions, entityPath)) {
    return entityPath;
  }
  return DEFAULT_NO_ENTITY_PERMISSION_PATH;
}

/** auth/me 응답 또는 user 객체에서 jurisdiction·permissions 추출 후 랜딩 경로 결정 */
export function resolveDefaultLandingPathFromAuth(
  authDataOrUser: any,
  jurisdiction?: string | null | undefined,
): string {
  const perms =
    authDataOrUser?.permissions
    || authDataOrUser?.user?.permissions
    || authDataOrUser?.data?.user?.permissions
    || null;
  const j =
    jurisdiction
    ?? authDataOrUser?.customer?.jurisdiction
    ?? authDataOrUser?.user?.customer?.jurisdiction
    ?? authDataOrUser?.data?.user?.customer?.jurisdiction
    ?? null;
  return resolveDefaultLandingPath(perms, j);
}
